import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, TrendingDown, DollarSign, Target } from 'lucide-react';

const problems = [
  {
    id: 'problem-random',
    icon: Target,
    title: 'Random Marketing',
    description: 'Scattered efforts across channels with no unified strategy or measurement.',
    stat: '73%',
    statLabel: 'of businesses lack a marketing strategy'
  },
  {
    id: 'problem-inconsistent',
    icon: TrendingDown,
    title: 'Inconsistent Leads',
    description: 'Feast or famine lead flow that makes revenue forecasting impossible.',
    stat: '61%',
    statLabel: 'struggle with lead generation'
  },
  {
    id: 'problem-conversion',
    icon: AlertTriangle,
    title: 'Low Conversion Rates',
    description: 'Traffic that never turns into customers due to broken conversion paths.',
    stat: '2.35%',
    statLabel: 'average website conversion rate'
  },
  {
    id: 'problem-adspend',
    icon: DollarSign,
    title: 'Wasted Ad Spend',
    description: 'Burning money on ads without proper tracking or optimization.',
    stat: '$37B',
    statLabel: 'wasted on ads annually'
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  },
};

export default function ProblemSection() {
  return (
    <section 
      id="problem"
      data-testid="problem-section"
      className="section-padding relative overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#0a0a0a] to-[#030303]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">
            The Problem
          </span>
          <h2 
            data-testid="problem-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Why Most Marketing
            <br />
            <span className="gradient-text">Falls Flat</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg">
            Without a systematic approach, businesses waste time and money on tactics 
            that never compound into real growth.
          </p>
        </motion.div>
        
        {/* Problem Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
        >
          {problems.map((problem, index) => (
            <motion.div
              key={problem.id}
              data-testid={`problem-card-${index}`}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-2xl glass p-8 card-hover"
            >
              {/* Gradient border effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-transparent" />
              </div>
              
              <div className="relative z-10">
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6 group-hover:bg-red-500/20 transition-colors">
                  <problem.icon className="w-6 h-6 text-red-400" />
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-3">
                  {problem.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  {problem.description}
                </p>
                
                {/* Stat */}
                <div className="flex items-end gap-2 pt-4 border-t border-white/10">
                  <span className="text-3xl font-bold text-red-400">{problem.stat}</span>
                  <span className="text-xs text-gray-500 pb-1">{problem.statLabel}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Transition Arrow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex justify-center mt-16"
        >
          <div className="w-px h-24 bg-gradient-to-b from-red-500/50 via-purple-500/50 to-cyan-500/50" />
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center text-gray-500 text-sm mt-6"
        >
          There&apos;s a better way...
        </motion.p>
      </div>
    </section>
  );
}
